import { type ReactNode } from 'react';
import { Zap, Link2 } from 'lucide-react';
import { Badge } from '@/shared/ui/Badge';
import { ClickableCard } from '@/shared/ui/ClickableCard';
import styles from './CampaignBadges.module.css';

/**
 * Visual distinction badges for PracticeNova-created vs externally-imported campaigns.
 * Used everywhere a campaign is rendered (Campaigns tab, Activity log, Detail page header,
 * budget rebalance, etc).
 */
export function PNBadge() {
  return (
    <Badge variant="success" className={styles.brandBadge}>
      <Zap size={12} aria-hidden />
      PN
    </Badge>
  );
}

export function ExtBadge() {
  return (
    <Badge variant="info" className={styles.brandBadge}>
      <Link2 size={12} aria-hidden />
      External
    </Badge>
  );
}

type StatusVariant = 'success' | 'warning' | 'neutral' | 'error';

function statusDisplay(status: string): { label: string; variant: StatusVariant } {
  switch (status) {
    case 'live':
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'approved':
      return { label: 'Approved', variant: 'success' };
    case 'launching':
      return { label: 'Launching', variant: 'warning' };
    case 'pending_creative':
      return { label: 'Pending Creative', variant: 'warning' };
    case 'ready_for_review':
      return { label: 'Ready for Review', variant: 'warning' };
    case 'paused':
      return { label: 'Paused', variant: 'neutral' };
    case 'dismissed':
      return { label: 'Dismissed', variant: 'neutral' };
    default:
      return { label: status.replace(/_/g, ' '), variant: 'neutral' };
  }
}

export function CampaignStatusBadge({ status }: { status: string }) {
  const { label, variant } = statusDisplay(status);
  return <Badge variant={variant}>{label}</Badge>;
}

/**
 * Card wrapper that owns the colored 3px left border for PN vs External campaigns.
 * Use this on any campaign card surface — Campaigns tab, autopilot wizard plan
 * review, rebalance drawer, etc.
 */
export function CampaignCardWrapper({
  variant,
  onClick,
  children,
  className,
}: {
  variant: 'pn' | 'external';
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const variantClass = variant === 'pn' ? styles.cardWrapperPn : styles.cardWrapperExt;

  return (
    <ClickableCard onClick={onClick} className={[variantClass, className].filter(Boolean).join(' ')}>
      {children}
    </ClickableCard>
  );
}
